import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { forkJoin, map } from "rxjs";
import { NavService } from "src/app/services/nav.service";
import { StoreService } from "src/app/services/apps/store/store.service";
import { ProviderService } from "src/app/services/apps/providers/provider.service";
import { isUserOnboardingInProgress } from "src/app/pages/profile/profile.model";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { navItems as defaultNavItems } from "src/app/layouts/full/vertical/sidebar/sidebar-data";
import { NavItem } from "src/app/layouts/full/vertical/sidebar/nav-item/nav-item";

export const blockedNavItemsGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const navService = inject(NavService);
  const storeService = inject(StoreService);
  const providerService = inject(ProviderService);
  const profileService = inject(ProfileService);
  const targetUrl = state.url?.split("?")[0].split("#")[0] ?? "/";

  return forkJoin({
    stores: storeService.getCachedOrLoad(),
    providers: providerService.getCachedOrLoad(),
  }).pipe(
    map(({ stores, providers }) => {
      const userProfile = profileService.userProfile();
      const isOnboardingIncomplete = isUserOnboardingInProgress(userProfile);
      const hasDynamicData =
        stores.length > 0 || providers.length > 0 || !!userProfile;
      const navItemSource = hasDynamicData
        ? navService.buildNavItemsWithDynamicClaims(
            defaultNavItems,
            stores,
            providers,
            isOnboardingIncomplete,
          ).navItems
        : navService.navItems();

      const isBlocked = isRouteBlocked(navItemSource, targetUrl);

      if (isBlocked) {
        return router.parseUrl("/dashboard");
      }

      return true;
    }),
  );
};

function isRouteBlocked(items: NavItem[], targetUrl: string): boolean {
  const hasUnblockedMatch = hasRouteMatch(items, targetUrl, false);
  const hasBlockedMatch = hasRouteMatch(items, targetUrl, true);

  return !hasUnblockedMatch && hasBlockedMatch;
}

function hasRouteMatch(
  items: NavItem[],
  targetUrl: string,
  block: boolean,
): boolean {
  for (const item of items) {
    if (
      item.route &&
      item.block === block &&
      routeMatches(targetUrl, item.route)
    ) {
      return true;
    }

    if (item.children && hasRouteMatch(item.children, targetUrl, block)) {
      return true;
    }
  }

  return false;
}

function routeMatches(targetUrl: string, itemRoute: string): boolean {
  const normalize = (value: string) => value.replace(/\/+$|^\s+|\s+$/g, "");
  const normalizedTarget = normalize(targetUrl.split("?")[0].split("#")[0]);
  const normalizedRoute = normalize(itemRoute.split("?")[0].split("#")[0]);

  return (
    normalizedTarget === normalizedRoute ||
    normalizedTarget.startsWith(normalizedRoute + "/")
  );
}
